import { Injectable } from "@angular/core";
import { EntityType } from "@models/common/entity-type.enum";
import { CellDataType } from "@models/grid/cell-data-type.enum";
import { ColDefCommunityExtended } from "@models/grid/grid-filter";
import { IndividualDto } from "@models/individuals/individual-dto";
import {
  AgIndividualContactComponent
} from "@pages/individuals/components/individual-profile-page/components/ag-individual-contact/ag-individual-contact.component";
import {
  AgIndividualEntityNameComponent
} from "@pages/individuals/components/individual-profile-page/components/ag-individual-entity-name/ag-individual-entity-name.component";
import {
  AgIndividulPobComponent
} from "@pages/individuals/components/individual-profile-page/components/ag-individul-pob/ag-individul-pob.component";
import {
  AgRelationshipNameAndRoleCellComponent
} from "@pages/individuals/components/individual-profile-page/components/ag-relationship-name-and-role-cell/ag-relationship-name-and-role-cell.component";
import {
  AgNonMatchingAddressCellComponent
} from "@pages/individuals/components/individual-profile-page/components/non-matching-cells/ag-mis-matching-address-cell/ag-non-matching-address-cell.component";
import {
  getRelationshipDatesFilterOptions,
  getRelationshipsContactNicknamesFilterOptions,
  relationshipsFilterOptions
} from "@pages/individuals/components/individual-profile-page/constants/individual-relationships-filter-options";
import {
  IndividualRelationshipGroupModel
} from "@pages/individuals/components/individual-profile-page/models/individual-relationship-group-model";
import { AgDateCellComponent } from "@shared/components/grid/ag-cells/common/ag-date-cell/ag-date-cell.component";
import { BaseGridService } from "@shared/services/base/base-grid.service";
import { getCommonActionCellColDef } from "@shared/utils/grid/get-common-column-chooser-params";
import { CellClassParams, ColDef } from "ag-grid-community";

@Injectable()
export class IndividualProfileGridService extends BaseGridService {
  setColDefsWithIndividualDtoAndActions(
    actionColumn: ColDef,
    individualDto: IndividualDto,
    changeRelationshipIndividualAddress: Function,
  ) {
    const colDefs = [
      {
        headerName: 'Name & Role',
        field: 'types',
        sortable: false,
        suppressHeaderMenuButton: true,
        minWidth: 150,
        flex: 1,
        cellRenderer: AgRelationshipNameAndRoleCellComponent,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: relationshipsFilterOptions,
        },
      },
      {
        headerName: 'Entity Name',
        field: 'entityName',
        sortable: false,
        suppressHeaderMenuButton: true,
        minWidth: 150,
        flex: 1,
        cellRenderer: AgIndividualEntityNameComponent,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Address',
        field: 'address.normalizedFullAddress',
        sortable: false,
        suppressHeaderMenuButton: true,
        minWidth: 120,
        flex: 1,
        cellRenderer: AgNonMatchingAddressCellComponent,
        cellRendererParams: {
          updateNonMatchingEntity: changeRelationshipIndividualAddress.bind(this),
          valueToMatch: individualDto.address.normalizedFullAddress
        },
        cellClass: (params: CellClassParams<IndividualRelationshipGroupModel>) => {
          if (params.data?.relatedToEntityType !== EntityType.Company || params.data?.isResigned) return null;

          const relationshipAddress = params.data?.address.normalizedFullAddress;
          return relationshipAddress?.toLowerCase() != individualDto.address.normalizedFullAddress.toLowerCase() ? 'non-match' : null;
        },
      },
      {
        headerName: 'Date of Birth',
        field: 'dob',
        minWidth: 130,
        width: 130,
        flex: 0,
        sortable: false,
        suppressHeaderMenuButton: true,
        cellRenderer: AgDateCellComponent,
        cellDataType: CellDataType.DATE_STRING,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: getRelationshipDatesFilterOptions(individualDto.relationshipIndividualDtoList)
        },
      },
      {
        headerName: 'Place of Birth',
        field: 'birthCountry',
        sortable: false,
        suppressHeaderMenuButton: true,
        cellRenderer: AgIndividulPobComponent,
        minWidth: 130,
        width: 130,
      },
      {
        headerName: 'Appointment Date',
        field: 'start',
        width: 150,
        flex: 1,
        cellRenderer: AgDateCellComponent,
      },
      {
        headerName: 'Cessation Date',
        field: 'end',
        width: 150,
        flex: 1,
        cellRenderer: AgDateCellComponent,
      },
      {
        headerName: 'Contact',
        field: 'contacts',
        flex: 1,
        sortable: false,
        suppressHeaderMenuButton: true,
        minWidth: 105,
        width: 105,
        cellRenderer: AgIndividualContactComponent,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: getRelationshipsContactNicknamesFilterOptions(individualDto.relationshipIndividualDtoList)
        },
      },
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true, })) as ColDefCommunityExtended[];

    const actionColDef = {
      ...getCommonActionCellColDef(colDefs),
      ...actionColumn
    };

    this.colDefs.set([...colDefs, actionColDef]);
  }
}
